import React, { useState, useCallback, useMemo } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Link, useLocation } from 'react-router';

import ColorSwatch from '@src/components/collaterals/ColorSwatch';
import ResourceTooltip from '@src/components/common/Display/ResourceTooltip';
import StatusLabels from '@src/components/common/Display/titles/StatusLabels';
import { useTrackDownload } from '@src/hooks/useTrackDownload';
import {
    getMonthDayYearWithNoLeadingZeros,
    SafelyRenderHtml,
} from '@src/services';
import { getImageSource } from '@src/utils/getImage';
import { isSupportedVariantColor } from '@src/utils/parseVariantColor';


import './TileContainerLarge.scss';

/**
 * ArticlesCard Promo card for articles
 * @param {object} productArticles Redux training courses data
 * @param {string} title Card Title
 * @param {string} imagesrc Image source
 * @param {string} imagealt Image alt text
 * @param {string} readTime Reading time
 * @param {string} linktotrainging Link to training
 * @param {array} agents Agent types for article
 * @param {array} states States avail for article
 * @param {string} published Published date
 * @param {string} pushHistory Redux app history
 */

const TileContainerLarge = (props) => {
    const {
        buttonLabel,
        description,
        ends_at: endsAt,
        expired_at: expiredAt,
        id,
        index,
        products,
        published_at: publishedAt,
        readTime,
        resource_type: resourceType,
        slug,
        starts_at: startsAt,
        states,
        status,
        title,
        urlPrefix,
        isImageShow = false,
        image,
        imageUrl,
        category = null,
        isStreched = true,
        isCopyText = false,
        isCustomizable = false,
        columns = 4,
        product_variants: productVariants = [],
    } = props;

    const { pathname } = useLocation();
    const { trackDownload } = useTrackDownload();

    let thisUrlPrefix;

    if (urlPrefix) {
        thisUrlPrefix = urlPrefix;
    } else {
        thisUrlPrefix = pathname === '/' ? '' : pathname;
    }

    thisUrlPrefix = thisUrlPrefix.charAt(thisUrlPrefix.length - 1) === '/' ? thisUrlPrefix.slice(0, -1) : thisUrlPrefix;

    const articleLink = slug ? `${thisUrlPrefix}/${slug}` : '?collateral_category=social-media';
    const isWebinar = (thisUrlPrefix && thisUrlPrefix.includes('webinars')) || resourceType === 'Webinar';

    const label = buttonLabel ? buttonLabel : 'Read More';
    const cardTitle = label === 'Read More' ? `Read more about ${title}` : title;
    const bodyText = () => {
        if (
            description
            && description.props
            && description.props.children
            && description.props.children === 'null'
        ) {
            return '';
        }

        let descriptionText = description;
        // if isCustomizable is true, then we need to parse the description and
        // replace all containing special tags with the default value or the fallback value if present,
        // examples:
        //  - {agency_name || "Our Agency"} or
        //  - {agency_name || 'Our Team'} or
        //  - {agency_name}

        if (isCustomizable) {
            const agencyNameRegex = /\{agency_name(?:\s*\|\|\s*['"]([^'"]+)['"])?\}/;
            descriptionText = agencyNameRegex.test(descriptionText)
                ? descriptionText.replace(/\{agency_name(?:\s*\|\|\s*['"]([^'"]+)['"])?\}/g, (match, fallback) => {
                    return fallback || 'Our Agency';
                })
                : descriptionText;
        }
        return <p>{descriptionText}</p>;
    };

    let classNames = 'col-sm-12 col-md-4 col-lg-3 mb-3 mb-md-4 mb-lg-4';
    if (columns === 3) {
        classNames = 'col-sm-12 col-md-6 col-lg-4 mb-3 mb-md-4 mb-lg-4';
    }

    const imgClassNames = classnames('rounded-0', {
        'brochures': category?.name?.toLowerCase().includes('brochure'),
        'quote-letters': category?.name?.toLowerCase().includes('quote'),
        'swag': category?.name?.toLowerCase().includes('swag'),
    });
    const imgSource = imageUrl ? imageUrl : getImageSource(image, 700);

    const [copied, setCopied] = useState(false);

    const handleDownloadClick = useCallback(() => {
        trackDownload(resourceType, id);
    }, [resourceType, id, trackDownload]);

    const variantColors = useMemo(() => {
        if (!category?.name?.toLowerCase().includes('swag') || !productVariants) return [];

        const colorVariants = productVariants.filter(
            (v) => v.use_as_preview_image || v.name?.toLowerCase().includes('color'),
        );

        const allColors = colorVariants.flatMap((v) => v.options?.map((o) => o.value) || []);
        const uniqueColors = [...new Set(allColors)];

        return uniqueColors.filter((color) => isSupportedVariantColor(color));
    }, [category, productVariants]);

    return (
        <div id={`components-promo-cards-quarter-articles-articles-card-${index}`} className={classNames}>
            <Card className="card-tile-container-large">
                {
                    isImageShow && imgSource
                    && <Card.Img
                        as={Link}
                        to={articleLink}
                        variant="top"
                        className={imgClassNames}
                        style={{ backgroundImage: `url(${imgSource})` }}
                    />
                }
                <Card.Body>
                    <Card.Title as="div" />
                    <h5 className="title">
                        <StatusLabels expiredAt={expiredAt} status={status} publishedAt={publishedAt} />
                        {SafelyRenderHtml(title)}
                    </h5>
                    {
                        ((startsAt && endsAt) || isWebinar) && (
                            <div className="start-end">
                                <span>
                                    {
                                        (startsAt && endsAt)
                                            ? `${getMonthDayYearWithNoLeadingZeros(startsAt)} - ${getMonthDayYearWithNoLeadingZeros(endsAt)}`
                                            : 'Dates and times will vary'
                                    }
                                </span>
                            </div>
                        )
                    }
                    { bodyText() }
                </Card.Body>
                <Card.Footer className="border-0 pb-4">
                    {
                        category && (
                            <div className="category-block">
                                {/* <div key={category.id} className="category-indicator">
                                    <span className={`category-box ${category.color || 'blue'}`} />
                                    <span className="category-text">{category.name}</span>
                                </div> */}
                                {variantColors.length > 0 && (
                                    <>
                                        <p className="variant-label">Color options:</p>
                                        <div className="variant-colors">
                                            {variantColors.map((color) => (
                                                <ColorSwatch
                                                    key={color}
                                                    color={color}
                                                    variant="icon"
                                                    title={color}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    }
                    {
                        isCustomizable && (
                            <p>
                                <Link
                                    to={articleLink}
                                    className={classnames('btn btn-outline-primary', { 'btn-less-padding': isCustomizable })}
                                    title="Customize Now"
                                    aria-label="Customize Now"
                                >
                                    Customize Now
                                </Link>
                            </p>
                        )
                    }
                    {
                        isStreched && (
                            <Link
                                to={articleLink}
                                className={classnames('btn btn-outline-primary stretched-link', { 'btn-less-padding': isCustomizable })}
                                title={cardTitle}
                                aria-label={cardTitle}
                            >
                                {readTime ? `${readTime} Minute Read` : `${label}`}
                            </Link>
                        )
                    }
                    {
                        !isStreched && (
                            <a
                                href={imageUrl}
                                className={classnames('btn btn-outline-primary', { 'btn-less-padding': isCustomizable })}
                                target="_blank"
                                rel="noreferrer"
                                onClick={handleDownloadClick}
                            >
                                {label}
                            </a>
                        )
                    }
                    {
                        isCopyText && (
                            <CopyToClipboard
                                text={description}
                                onCopy={() => setCopied(true)}
                            >
                                <Button
                                    variant="outline-primary"
                                    className={classnames('float-end', { 'btn-less-padding': isCustomizable })}
                                    title="Copy Text"
                                    aria-label="Copy Text"
                                >
                                    {copied ? 'Copied' : 'Copy Text'}
                                </Button>
                            </CopyToClipboard>
                        )
                    }
                    {
                        !isCopyText && (
                            <ResourceTooltip
                                placement="left"
                                products={products}
                                publishedAt={publishedAt}
                                states={states}
                            />
                        )
                    }
                </Card.Footer>
            </Card>
        </div>
    );
};

TileContainerLarge.propTypes = {
    buttonLabel: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ends_at: PropTypes.string,
    expired_at: PropTypes.string,
    id: PropTypes.number,
    isImageShow: PropTypes.bool,
    image: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        source: PropTypes.string.isRequired,
        versions: PropTypes.shape({
            w400: PropTypes.string,
            w700: PropTypes.string,
            w1200: PropTypes.string,
        }),
    }),
    imageUrl: PropTypes.string,
    index: PropTypes.number.isRequired,
    products: PropTypes.arrayOf(PropTypes.shape({
        states: PropTypes.arrayOf(PropTypes.object),
    })),
    published_at: PropTypes.string,
    readTime: PropTypes.number,
    slug: PropTypes.string,
    starts_at: PropTypes.string,
    states: PropTypes.array,
    status: PropTypes.string.isRequired,
    title: PropTypes.string,
    urlPrefix: PropTypes.string,
    category: PropTypes.object,
    isStreched: PropTypes.bool,
    isCopyText: PropTypes.bool,
    columns: PropTypes.number,
    product_variants: PropTypes.arrayOf(PropTypes.object),
};

export default TileContainerLarge;
